import React, { useState, useEffect } from 'react';
import axios from 'axios';

const RuleForm = ({ schema = {}, rules, editingRule = null, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    fieldName: '',
    type: 'prompt',
    dependsOn: '',
    config: {},
    model: 'gpt-4.1',
  });
  
  const [tempFieldsInput, setTempFieldsInput] = useState('');
  
  const [customFunctions, setCustomFunctions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Initialize form if editing an existing rule
  useEffect(() => {
    if (editingRule) {
      setIsEditing(true);
      
      // Format dependsOn array to comma-separated string
      const dependsOnString = editingRule.dependsOn ? editingRule.dependsOn.join(', ') : '';
      
      // Set form data based on the rule being edited
      setFormData({
        fieldName: editingRule.fieldName || '',
        type: editingRule.type || 'prompt',
        dependsOn: dependsOnString,
        config: editingRule.config || {},
        model: editingRule.model || 'gpt-4.1',
        _id: editingRule._id // Keep the ID for updating
      });
      
      // If this is a combineFields rule, initialize the tempFieldsInput
      if (editingRule.type === 'customCode' && 
          editingRule.config.function === 'combineFields' && 
          Array.isArray(editingRule.config.fields)) {
        setTempFieldsInput(editingRule.config.fields.join(', '));
      }
    } else {
      // Reset form when not editing
      setIsEditing(false);
      setFormData({
        fieldName: '',
        type: 'prompt',
        dependsOn: '',
        config: {},
        model: 'gpt-4.1',
      });
      setTempFieldsInput('');
    }
  }, [editingRule]);

  useEffect(() => {
    if (formData.config.function === 'combineFields') {
      if (formData.config.fields && Array.isArray(formData.config.fields)) {
        setTempFieldsInput(formData.config.fields.join(', '));
      } else {
        setTempFieldsInput('');
      }
    }
  }, [formData.config.function]);

  useEffect(() => {
    const fetchCustomFunctions = async () => {
      try {
        const simulatedFunctions = [
          'extractDomainFromEmail',
          'combineFields',
          'calculateSentiment',
          'categorizeCustomer',
          'arrayToString',
          'removeFields',
        ];
        setCustomFunctions(simulatedFunctions);
      } catch (error) {
        console.error('Error fetching custom functions:', error);
      }
    };

    if (formData.type === 'customCode') {
      fetchCustomFunctions();
    }
  }, [formData.type]);

  useEffect(() => {
    // When function changes to removeFields, clear the fieldName
    if (formData.config.function === 'removeFields') {
      setFormData(prev => ({
        ...prev,
        fieldName: ''
      }));
    }
  }, [formData.config.function]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleConfigChange = (key, value) => {
    setFormData({
      ...formData,
      config: { ...formData.config, [key]: value },
    });
  };

  const handleFieldsInputChange = (e) => {
    const value = e.target.value;
    setTempFieldsInput(value);
    
    const fieldsArray = value.split(',').map(item => item.trim()).filter(item => item !== '');
    handleConfigChange('fields', fieldsArray);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    
    try {
      if (!formData.fieldName) {
        setError('Field name is required');
        return;
      }

      switch (formData.type) {
        case 'prompt':
          if (!formData.config.prompt) {
            setError('Prompt text is required');
            return;
          }
          break;
        case 'expression':
          if (!formData.config.expression) {
            setError('Expression is required');
            return;
          }
          break;
        case 'customCode':
          if (!formData.config.function) {
            setError('Function selection is required');
            return;
          }
          break;
        default:
          break;
      }

      const dependsOnArray = formData.dependsOn
        ? formData.dependsOn.split(',').map(item => item.trim()).filter(item => item !== '')
        : [];

      onSubmit({
        ...formData,
        dependsOn: dependsOnArray
      });
    } catch (error) {
      setError('An unexpected error occurred');
    }
  };

  const renderTypeSpecificFields = () => {
    switch (formData.type) {
      case 'prompt':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
              <select
                name="model"
                value={formData.model}
                onChange={handleChange}
                className="block w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="gpt-4.1">GPT-4.1</option>
                <option value="gpt-5.1">GPT-5.1</option>
                <option value="gpt-4.1-mini">GPT-4.1 Mini</option>
                <option value="gpt-4.1-nano">GPT-4.1 Nano</option>
                <option value="o4-mini">o4-mini (Reasoning)</option>
              </select>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Prompt</label>
              <textarea
                value={formData.config.prompt || ''}
                onChange={(e) => handleConfigChange('prompt', e.target.value)}
                rows={4}
                className="block w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                placeholder="Write a prompt that describes how to generate the field value..."
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
              <select
                value={formData.config.fieldType || 'string'}
                onChange={(e) => handleConfigChange('fieldType', e.target.value)}
                className="block w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="string">String</option>
                <option value="number">Number</option>
                <option value="json">JSON</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Determines how the AI output will be parsed
              </p>
            </div>
            
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.config.passEntireJson === true}
                  onChange={(e) => handleConfigChange('passEntireJson', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mr-2"
                />
                <span className="text-sm text-gray-900">Pass entire JSON to the prompt</span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                When enabled, the entire data object will be included in the prompt in addition to substituting placeholders
              </p>
            </div>
          </>
        );
        
      case 'expression':
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Expression</label>
            <input
              type="text"
              value={formData.config.expression || ''}
              onChange={(e) => handleConfigChange('expression', e.target.value)}
              className="block w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="data.field1 + data.field2 (not fully implemented yet)"
            />
            <p className="text-xs text-gray-500 mt-1">
              Note: Expression type is not fully implemented yet
            </p>
          </div>
        );
        
      case 'lookup':
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Lookup Source</label>
            <input
              type="text"
              value={formData.config.source || ''}
              onChange={(e) => handleConfigChange('source', e.target.value)}
              className="block w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="e.g., API URL or database table (not fully implemented yet)"
            />
            <p className="text-xs text-gray-500 mt-1">
              Note: Lookup type is not fully implemented yet
            </p>
          </div>
        );
        
      case 'customCode':
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Function</label>
            <select
              value={formData.config.function || ''}
              onChange={(e) => handleConfigChange('function', e.target.value)}
              className="block w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">Select a function</option>
              {customFunctions.map((func) => (
                <option key={func} value={func}>
                  {func}
                </option>
              ))}
            </select>
            
            {formData.config.function === 'extractDomainFromEmail' && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Field</label>
                <input
                  type="text"
                  value={formData.config.emailField || ''}
                  onChange={(e) => handleConfigChange('emailField', e.target.value)}
                  className="block w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Enter field name (e.g. email or user.email)"
                />
              </div>
            )}
            
            {formData.config.function === 'combineFields' && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Fields to Combine (comma-separated)</label>
                <input
                  type="text"
                  value={tempFieldsInput}
                  onChange={handleFieldsInputChange}
                  className="block w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  placeholder="firstName, lastName, address.city"
                />
                <div className="mt-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Separator</label>
                  <input
                    type="text"
                    value={formData.config.separator || ' '}
                    onChange={(e) => handleConfigChange('separator', e.target.value)}
                    className="block w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    placeholder="Space, comma, etc."
                  />
                </div>
              </div>
            )}
            
            {formData.config.function === 'calculateSentiment' && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Text Field</label>
                <input
                  type="text"
                  value={formData.config.textField || ''}
                  onChange={(e) => handleConfigChange('textField', e.target.value)}
                  className="block w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Enter field name (e.g. comment or review.text)"
                />
              </div>
            )}
            
            {formData.config.function === 'categorizeCustomer' && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Amount Field</label>
                <input
                  type="text"
                  value={formData.config.purchaseAmountField || ''}
                  onChange={(e) => handleConfigChange('purchaseAmountField', e.target.value)}
                  className="block w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Enter field name (e.g. amount or order.total)"
                />
              </div>
            )}
            
            {formData.config.function === 'arrayToString' && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Array Field</label>
                <input
                  type="text"
                  value={formData.config.field || ''}
                  onChange={(e) => handleConfigChange('field', e.target.value)}
                  className="block w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Enter field name (e.g. tags or items)"
                />
                <div className="mt-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Delimiter</label>
                  <input
                    type="text"
                    value={formData.config.delimiter || ','}
                    onChange={(e) => handleConfigChange('delimiter', e.target.value)}
                    className="block w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    placeholder="Comma, space, etc."
                  />
                </div>
              </div>
            )}
            
            {formData.config.function === 'removeFields' && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Fields to Remove (comma-separated)</label>
                <input
                  type="text"
                  value={formData.config.fields || ''}
                  onChange={(e) => handleConfigChange('fields', e.target.value)}
                  className="block w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Enter field names (e.g. password, sensitive_data)"
                />
              </div>
            )}
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 p-4 rounded-md">
          <div className="text-sm text-red-600">{error}</div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Field Name</label>
          <input
            type="text"
            name="fieldName"
            value={formData.fieldName}
            onChange={handleChange}
            className="block w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            placeholder={"Enter field name (can use dot notation for nested fields)"}
            required={true}
          />
          {formData.config.function === 'removeFields' && (
            <p className="text-xs text-gray-500 mt-1">
              No field name needed - put any name, it will be ignored.
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Rule Type</label>
          <select
            name="type"
            value={formData.type}
            onChange={handleChange}
            className="block w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            required
          >
            <option value="prompt">AI Prompt</option>
            <option value="customCode">Custom Code</option>
            <option value="expression">Expression</option>
            <option value="lookup">Lookup</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Depends On (Optional)
        </label>
        <input
          type="text"
          name="dependsOn"
          value={formData.dependsOn}
          onChange={handleChange}
          className="block w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500" 
          placeholder="Comma-separated field names (e.g. firstName, lastName, address.city)"
        />
        <p className="text-xs text-gray-500 mt-1">
          List fields this rule depends on, separated by commas
        </p>
      </div>

      {renderTypeSpecificFields()}

      <div className="flex justify-end space-x-3 mt-6">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-md font-medium bg-gray-200 text-gray-800 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 rounded-md font-medium bg-primary-600 text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
          disabled={loading}
        >
          {loading ? (isEditing ? 'Updating...' : 'Adding...') : (isEditing ? 'Update Rule' : 'Add Rule')}
        </button>
      </div>
    </form>
  );
};

export default RuleForm;